import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { hitPage } from "@/lib/util";
import { gaPageView } from "@/lib/ga";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { StoreProvider } from "@/context/Store";
import useAutoUpdate from "@/lib/useAutoUpdate";
import ShopLayout from "@/components/shop/Layout";
import Home from "@/pages/Home";
import Listing from "@/pages/Listing";
import Product from "@/pages/Product";
import Checkout, { CartPage, Confirmation } from "@/pages/Checkout";
import Custom from "@/pages/Custom";
import Reviews from "@/pages/Reviews";
import Club, { ClubThanks } from "@/pages/Club";
import { NotFound, CmsPage, Resolve, Collections, Magazine, Post, Contact, FaqPage, Wishlist, Tracking, Login, ResetPassword, AuthCallback, Account, LocalPage } from "@/pages/Misc";

const Admin = lazy(() => import("@/admin/AdminApp"));

const RedirectPost = () => { const { slug } = useParams(); return <Navigate to={`/blog/${slug}`} replace />; };

function Router() {
  const loc = useLocation();
  useAutoUpdate();
  useEffect(() => { if (!loc.pathname.startsWith("/admin")) hitPage(loc.pathname); }, [loc.pathname]);
  useEffect(() => { const t = setTimeout(() => gaPageView(document.title), 1500); return () => clearTimeout(t); }, [loc.pathname, loc.search]);
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/admin/*" element={<Suspense fallback={<div className="p-10">Chargement…</div>}><Admin /></Suspense>} />
      <Route element={<ShopLayout />}>
        <Route index element={<Home />} />
        <Route path="boutique" element={<Listing mode="all" />} />
        <Route path="nouveautes" element={<Listing mode="all" />} />
        <Route path="meilleures-ventes" element={<Listing mode="all" />} />
        <Route path="promotions" element={<Listing mode="all" />} />
        <Route path="recherche" element={<Listing mode="search" />} />
        <Route path="collections" element={<Collections />} />
        <Route path="categorie/:slug" element={<Listing mode="category" />} />
        <Route path="collection/:slug" element={<Listing mode="collection" />} />
        <Route path="marque/:slug" element={<Listing mode="brand" />} />
        <Route path="produit/:slug" element={<Product />} />
        <Route path="page/:slug" element={<CmsPage />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="avis" element={<Reviews />} />
        <Route path="club" element={<Club />} />
        <Route path="club/merci" element={<ClubThanks />} />
        <Route path="faq/:slug" element={<FaqPage />} />
        <Route path="blog" element={<Magazine />} />
        <Route path="blog/:slug" element={<Post />} />
        <Route path="magazine" element={<Navigate to="/blog" replace />} />
        <Route path="magazine/:slug" element={<RedirectPost />} />
        <Route path="contact" element={<Contact />} />
        <Route path="wishlist" element={<Wishlist />} />
        <Route path="panier" element={<CartPage />} />
        <Route path="checkout" element={<Checkout />} />
        <Route path="confirmation" element={<Confirmation />} />
        <Route path="suivi-commande" element={<Tracking />} />
        <Route path="connexion" element={<Login />} />
        <Route path="inscription" element={<Login register />} />
        <Route path="reinitialiser-mot-de-passe" element={<ResetPassword />} />
        <Route path="mon-compte" element={<Account />} />
        <Route path="mon-compte/commandes" element={<Account tab="commandes" />} />
        <Route path="mon-compte/commandes/:id" element={<Account tab="commandes" />} />
        <Route path="mon-compte/favoris" element={<Account tab="favoris" />} />
        <Route path="mon-compte/adresses" element={<Account tab="adresses" />} />
        <Route path="mon-compte/profil" element={<Account tab="profil" />} />
        <Route path="mon-compte/suivi" element={<Account tab="suivi" />} />
        <Route path="mon-compte/factures" element={<Account tab="factures" />} />
        <Route path="mon-compte/club" element={<Account tab="club" />} />
        <Route path="bijouterie-en-ligne/:city" element={<LocalPage />} />
        <Route path="bijoux-personnalises" element={<Custom />} />
        <Route path=":slug" element={<Resolve />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    if (!("serviceWorker" in navigator) || window.location.protocol !== "https:") return;
    const had = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener("controllerchange", () => { if (had && !sessionStorage.getItem("sw_reloaded")) { sessionStorage.setItem("sw_reloaded", "1"); window.location.reload(); } });
    navigator.serviceWorker.register("/sw.js").then((r) => r.update()).catch(() => {});
  }, []);
  return (
    <BrowserRouter>
      <StoreProvider><Router /><Toaster position="top-center" /></StoreProvider>
    </BrowserRouter>
  );
}
